import { useCallback, useRef, useState } from 'react';
import { ALE_TEXTS } from '../config';
import { aleClient } from '../lib/aleClient';
import { useAleSession } from './useAleSession';
import { buildDoubtWhatsAppLink, buildAttendantWhatsAppLink } from '../lib/whatsapp';

let idSeq = 0;
const nextId = () => `msg-${Date.now()}-${idSeq++}`;

const makeMsg = (role, text, extra = {}) => ({
  id: nextId(),
  role, // 'user' | 'assistant'
  text,
  ...extra,
});

// Histórico enviado ao backend (só role + text das últimas trocas).
const toHistory = (messages) =>
  messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .slice(-8)
    .map((m) => ({ role: m.role, text: m.text }));

export function useAleChat() {
  const { ensureSession, clearSession } = useAleSession();
  const [messages, setMessages] = useState(() => [
    makeMsg('assistant', ALE_TEXTS.greeting),
  ]);
  const [isSending, setIsSending] = useState(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const pushAssistant = useCallback((text, extra) => {
    setMessages((prev) => [...prev, makeMsg('assistant', text, extra)]);
  }, []);

  // Chama o backend (IA) uma vez, com auto-refresh de sessão em caso de 401.
  const callBackend = useCallback(
    async (text, historico, retry = true) => {
      const token = await ensureSession();
      try {
        const { data } = await aleClient.sendMessage(text, historico, token);
        return data;
      } catch (error) {
        if (error?.response?.status === 401 && retry) {
          clearSession();
          await ensureSession(true);
          return callBackend(text, historico, false);
        }
        throw error;
      }
    },
    [ensureSession, clearSession]
  );

  // Mensagem digitada pelo usuário → vai para a IA no backend.
  const sendMessage = useCallback(
    async (rawText) => {
      const text = (rawText || '').trim();
      if (!text || isSending) return;

      const userMsg = makeMsg('user', text);
      const historico = toHistory([...messagesRef.current, userMsg]);
      setMessages((prev) => [...prev, userMsg]);
      setIsSending(true);

      try {
        const data = await callBackend(text, historico);
        // Quando o Ale precisa encaminhar, o botão do WhatsApp já vai com a
        // dúvida do cliente embutida (o "text" que ele acabou de digitar).
        pushAssistant(data?.resposta || ALE_TEXTS.errorMsg, {
          whatsappUrl: data?.encaminharWhatsapp ? buildDoubtWhatsAppLink(text) : undefined,
        });
      } catch (error) {
        pushAssistant(ALE_TEXTS.errorMsg, {
          whatsappUrl: buildDoubtWhatsAppLink(text),
        });
      } finally {
        setIsSending(false);
      }
    },
    [isSending, callBackend, pushAssistant]
  );

  // Pergunta pronta (botão) → resposta estática instantânea, sem custo de IA.
  const sendQuickReply = useCallback(
    (quick) => {
      if (!quick) return;

      // "Falar com atendente" → só encaminha ao WhatsApp.
      if (quick.toWhatsapp) {
        setMessages((prev) => [...prev, makeMsg('user', quick.label)]);
        pushAssistant(
          'Claro! Vou te encaminhar para o nosso atendimento no WhatsApp. É só tocar no botão abaixo. 💬',
          { whatsappUrl: buildAttendantWhatsAppLink() }
        );
        return;
      }

      setMessages((prev) => [...prev, makeMsg('user', quick.label)]);
      if (quick.answer) {
        pushAssistant(quick.answer);
      }
    },
    [pushAssistant]
  );

  return { messages, isSending, sendMessage, sendQuickReply };
}
